import { List, Show, Create, Edit, Datagrid, TextField, DateField, ChipField, SimpleForm, TextInput, SelectInput, Button, Filter, SearchInput, useGetList, useUpdate, useNotify, useRefresh, ReferenceInput } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Chip, Stepper, Step, StepLabel, Alert, Grid, LinearProgress, List as MuiList, ListItem, ListItemIcon, ListItemText, Avatar, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, TablePagination, CircularProgress, IconButton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import type { TerminationRecord, ExitInterviewRecord } from '@/types/entities';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PersonRemove, CheckCircle, Schedule, Warning, ExitToApp, ArrowForward, Description, Badge, Security, VerifiedUser, Cancel, Person, HourglassEmpty, Close } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { ProcessActionsGrouped } from '@/components/workflow/ProcessActions';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';

const TERMINATION_TYPES = ['Resignation', 'Dismissal', 'Retirement', 'ContractEnd', 'IllHealth', 'Death', 'Absconded'];
const TERMINATION_STATUS = ['Initiated', 'PendingClearance', 'AwaitingConfirmation', 'Completed', 'Cancelled'];
const STATUS_COLORS: Record<string, string> = {
  Initiated: 'default',
  PendingClearance: 'warning',
  AwaitingConfirmation: 'info',
  Completed: 'success',
  Cancelled: 'error',
};
const TYPE_COLORS: Record<string, string> = {
  Resignation: 'info',
  Dismissal: 'error',
  Retirement: 'default',
  ContractEnd: 'default',
  IllHealth: 'warning',
  Death: 'default',
  Absconded: 'error',
};

const EXIT_CHECKLIST_ITEMS = [
  { id: 1, label: 'Laptop Returned', responsible: 'IT Admin', icon: 'laptop' },
  { id: 2, label: 'Access Card Returned', responsible: 'IT Admin', icon: 'card' },
  { id: 3, label: 'System De-provisioning', responsible: 'IT Admin', icon: 'lock' },
  { id: 4, label: 'Final Pay Approved', responsible: 'Finance', icon: 'money' },
  { id: 5, label: 'Certificate of Service Issued', responsible: 'HR', icon: 'cert' },
  { id: 6, label: 'UI19 Form Issued', responsible: 'HR', icon: 'doc' },
  { id: 7, label: 'Exit Interview Completed', responsible: 'HR', icon: 'interview' },
  { id: 8, label: 'Hand-over Completed', responsible: 'Manager', icon: 'handover' },
];

const STATUTORY_DOCUMENTS = [
  { doc: 'UI19 Form', act: 'UIF Act', required: true },
  { doc: 'Certificate of Service', act: 'BCEA Section 42', required: true },
  { doc: 'Exit Interview Record', act: 'Internal Policy', required: false },
];

const formatTerminationType = (type: string) => type?.replace(/([A-Z])/g, ' $1').trim() || type;

export const TerminationList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: terminations, isLoading } = useGetList('terminations', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const employeeMap = useEmployeeMap();

  const stats = {
    total: terminations?.length ?? 0,
    initiated: terminations?.filter((t: TerminationRecord) => t.status === 'Initiated').length ?? 0,
    pendingClearance: terminations?.filter((t: TerminationRecord) => t.status === 'PendingClearance').length ?? 0,
    awaitingConfirmation: terminations?.filter((t: TerminationRecord) => t.status === 'AwaitingConfirmation').length ?? 0,
    completed: terminations?.filter((t: TerminationRecord) => t.status === 'Completed').length ?? 0,
  };

  const typeCounts = terminations?.reduce((acc: Record<string, number>, t: TerminationRecord) => {
    acc[t.terminationType] = (acc[t.terminationType] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(typeCounts).map(([label, value]) => ({ label: formatTerminationType(label), value: Number(value) }));

  const statusCounts = terminations?.reduce((acc: Record<string, number>, t: TerminationRecord) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const barData = ['Initiated', 'PendingClearance', 'AwaitingConfirmation', 'Completed'].map(s => statusCounts[s] || 0);
  const barLabels = ['Initiated', 'Pending Clearance', 'Awaiting Confirmation', 'Completed'];

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.offboarding" wizardPath="wizard/offboarding" label="Offboarding Wizard" />
        </Box>
        <ListPageHeader
          title="Terminations / Offboarding"
          metrics={[
            { label: 'Pending Clearance', value: String(stats.pendingClearance), color: 'warning.main' },
            { label: 'Awaiting Confirmation', value: String(stats.awaitingConfirmation), color: 'info.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {terminations?.map((term: TerminationRecord) => (
            <Paper key={term.id} sx={{ p: 2 }} onClick={() => navigate(`/terminations/${term.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: TYPE_COLORS[term.terminationType], fontSize: 14 }}>
                    <PersonRemove sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, term.employee?.id)}</Typography>
                  </Box>
                </Box>
                <Chip label={term.status} size="small" color={STATUS_COLORS[term.status] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Chip label={formatTerminationType(term.terminationType)} size="small" color={TYPE_COLORS[term.terminationType] as ChipColor} />
                <Typography variant="caption" color="text.secondary">Last day: {term.effectiveDate}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">{term.reason}</Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.offboarding" wizardPath="wizard/offboarding" label="Offboarding Wizard" />
      </Box>
      <ListPageHeader
        title="Terminations / Offboarding"
        metrics={[
          { label: 'Pending Clearance', value: String(stats.pendingClearance), color: 'warning.main' },
          { label: 'Awaiting Confirmation', value: String(stats.awaitingConfirmation), color: 'info.main' },
          { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Termination Type"
        barChartData={barData}
        barChartLabels={barLabels}
        barChartTitle="By Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Effective Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {terminations?.map((term: TerminationRecord) => (
              <TableRow
                key={term.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/terminations/${term.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: TYPE_COLORS[term.terminationType], fontSize: 12 }}>
                      <Person sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, term.employee?.id)}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={formatTerminationType(term.terminationType)} size="small" color={TYPE_COLORS[term.terminationType] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Chip label={term.status} size="small" color={STATUS_COLORS[term.status] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{term.effectiveDate}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>{term.reason}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={terminations?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!terminations || terminations.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PersonRemove sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No terminations found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const TerminationFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search by name or employee #" />
    <SelectInput source="type" choices={TERMINATION_TYPES.map(t => ({ id: t, name: t }))} />
    <SelectInput source="status" choices={TERMINATION_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);

const STATUS_LABELS: Record<string, string> = {
  Initiated: 'Created',
  PendingClearance: 'Clearance',
  AwaitingConfirmation: 'Confirmation',
  Completed: 'Finalized',
};

const STATUS_STAGE_DEFS = [
  { id: 'Initiated', label: 'Created', icon: <Description fontSize="small" />, desc: 'Termination record created' },
  { id: 'PendingClearance', label: 'Clearance', icon: <Schedule fontSize="small" />, desc: 'Exit clearance in progress' },
  { id: 'AwaitingConfirmation', label: 'Confirmation', icon: <VerifiedUser fontSize="small" />, desc: 'Awaiting employee confirmation' },
  { id: 'Completed', label: 'Finalized', icon: <CheckCircle fontSize="small" />, desc: 'UI19 and Certificate issued' },
];

const TerminationShowContent = ({ record }: { record: TerminationRecord }) => {
  const navigate = useNavigate();
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const employeeMap = useEmployeeMap();

  const isPendingClearance = record.status === 'PendingClearance';
  const isCompleted = record.status === 'Completed';
  const isDismissal = record.terminationType === 'Dismissal';
  const stepIndex = ['Initiated', 'PendingClearance', 'AwaitingConfirmation', 'Completed'].indexOf(record.status || 'Initiated');

  const handleAction = (data: Record<string, unknown>) => {
    update('terminations', { id: record.id, data, previousData: record }, {
      onSuccess: () => { notify('Termination updated', { type: 'success' }); refresh(); },
      onError: (e: Error) => { notify(`Error: ${e.message || 'Update failed'}`, { type: 'error' }); },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/terminations')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #4a148c, #6a1b9a)', color: 'primary.contrastText', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 24 }}>
            <PersonRemove sx={{ fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" fontWeight={700}>Offboarding — {getEmployeeName(employeeMap, record.employee?.id) || 'Unknown'}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {record.terminationType === 'Dismissal' ? 'Dismissal' : record.terminationType === 'Resignation' ? 'Resignation' : record.terminationType === 'Retirement' ? 'Retirement' : record.terminationType} — {record.reason || 'No reason specified'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip label={record.status} size="small" color={STATUS_COLORS[record.status] as ChipColor || 'default'} sx={{ fontWeight: 600 }} />
              <Chip label={record.terminationType} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Effective Date</Typography>
            <Typography variant="h6" fontWeight={700}>{record.effectiveDate || '—'}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Employee</Typography>
            <Typography variant="body1" fontWeight={600}>{getEmployeeName(employeeMap, record.employee?.id) || '—'}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Notice Date</Typography>
            <Typography variant="body1" fontWeight={600}>{record.noticeDate || '—'}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>UI19</Typography>
            <Typography variant="body1" fontWeight={600}>{record.ui19Issued ? 'Issued' : 'Pending'}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120, textAlign: 'right' }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Certificate of Service</Typography>
            <Typography variant="body1" fontWeight={600}>{record.certificateOfServiceIssued ? 'Issued' : 'Pending'}</Typography>
          </Box>
        </Box>
      </Paper>

      <ProcessActionsGrouped
        groups={[
          {
            title: 'Termination Workflow',
            actions: [
              {
                label: 'Submit Termination',
                description: 'Submit the termination for HR review. Changes status to PendingClearance and notifies HR Manager.',
                icon: <CheckCircle />,
                color: 'success',
                condition: record.status === 'Initiated',
                onClick: () => handleAction({ status: 'PendingClearance' }),
                loading: isUpdating,
              },
              {
                label: 'Record Notice Served',
                description: 'Confirm that the employee has been given notice as per BCEA Section 38. Records the notice date.',
                icon: <Schedule />,
                color: 'info',
                condition: record.status === 'PendingClearance' || record.status === 'AwaitingConfirmation',
                onClick: () => handleAction({ status: 'AwaitingConfirmation' }),
                loading: isUpdating,
              },
              {
                label: 'Cancel Termination',
                description: 'Cancel this termination. Can only be done before processing is complete.',
                icon: <Cancel />,
                color: 'error',
                variant: 'outlined',
                condition: record.status === 'PendingClearance' || record.status === 'AwaitingConfirmation',
                onClick: () => handleAction({ status: 'Cancelled' }),
                loading: isUpdating,
              },
              {
                label: 'Complete Exit',
                description: 'Finalize the termination and process final pay. Records all checklist items and issues statutory documents.',
                icon: <CheckCircle />,
                color: 'success',
                condition: record.status === 'AwaitingConfirmation',
                onClick: () => handleAction({ status: 'Completed' }),
                loading: isUpdating,
              },
            ],
          },
          {
            title: 'Documents',
            actions: [
              {
                label: 'Download UI19',
                description: 'Download the UI19 form for UIF claims. Generated from termination details.',
                icon: <Description />,
                color: 'info',
                condition: record.status === 'Completed',
                onClick: () => window.open(`/api/terminations/${record.id}/ui19`, '_blank'),
              },
              {
                label: 'Certificate of Service',
                description: 'Download the Certificate of Service as required by BCEA Section 42.',
                icon: <Description />,
                color: 'inherit',
                variant: 'outlined',
                condition: record.status === 'Completed',
                onClick: () => window.open(`/api/terminations/${record.id}/certificate`, '_blank'),
              },
            ],
          },
        ]}
        isLoading={isUpdating}
      />

      {isDismissal && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          Dismissal — ensure all legal requirements per LRA Schedule 8 are met
        </Alert>
      )}
      {isPendingClearance && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<Schedule />}>
          Termination pending — complete offboarding checklist
        </Alert>
      )}
      {isCompleted && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Termination processed — UI19 and Certificate of Service issued
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Employee', value: getEmployeeName(employeeMap, record.employee?.id), bold: true },
          { label: 'Type', value: record.terminationType, chip: true },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Effective Date', value: record.effectiveDate },
          { label: 'Reason', value: record.reason },
          { label: 'Notice Date', value: record.noticeDate },
          { label: 'UI19 Issued', value: record.ui19Issued ? 'Yes' : 'No' },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" color={(f.label === 'Type' ? TYPE_COLORS : STATUS_COLORS)[f.value] as ChipColor} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Offboarding Progress</Typography>
        <Grid container spacing={2}>
          {STATUS_STAGE_DEFS.map((stage, idx) => {
            const isCompleted = idx < stepIndex;
            const isCurrent = idx === stepIndex;
            const isNext = idx === stepIndex + 1;
            const statusMap = ['Initiated', 'PendingClearance', 'AwaitingConfirmation', 'Completed'];
            const nextStatus = isNext ? statusMap[idx] : null;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stage.id}>
                <Card
                  variant="outlined"
                  onClick={() => isNext && handleAction({ status: nextStatus })}
                  sx={{
                    cursor: isNext ? 'pointer' : 'default',
                    height: '100%',
                    borderLeft: '4px solid',
                    borderColor: isCompleted ? 'success.main' : isCurrent ? 'primary.main' : 'grey.300',
                    opacity: isNext ? 1 : isCompleted || isCurrent ? 1 : 0.6,
                    '&:hover': isNext ? { boxShadow: 2 } : {},
                    transition: 'all 0.2s',
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: isCompleted ? 'success.main' : isCurrent ? 'primary.main' : 'grey.300' }}>
                          {stage.icon}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{stage.label}</Typography>
                      </Box>
                      {isCompleted ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : isCurrent ? (
                        <Schedule color="primary" fontSize="small" />
                      ) : (
                        <HourglassEmpty color="disabled" fontSize="small" />
                      )}
                    </Box>
                    <Typography variant="caption" color={isCompleted || isCurrent ? 'text.secondary' : 'text.disabled'} sx={{ mt: 0.5, display: 'block', ml: 5 }}>
                      {isCompleted ? stage.desc : isCurrent ? 'In progress' : isNext ? 'Click to advance' : 'Pending'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Exit Checklist</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {EXIT_CHECKLIST_ITEMS.map((item) => (
                <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <CheckCircle color="success" fontSize="small" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{item.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.responsible}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Statutory Documents</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {STATUTORY_DOCUMENTS.map(doc => (
                <Box key={doc.doc} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: doc.required ? 'info.light' : 'action.hover', borderRadius: 1 }}>
                  {doc.required ? <VerifiedUser color="info" fontSize="small" /> : <Badge fontSize="small" />}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{doc.doc}</Typography>
                    <Typography variant="caption" color="text.secondary">{doc.act} {doc.required ? '(Mandatory)' : '(Optional)'}</Typography>
                  </Box>
                  {record.status === 'Completed' && <CheckCircle color="success" fontSize="small" />}
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button component={Link} to="/employees" variant="outlined" size="small" startIcon={<PersonRemove />}>
          View Employee
        </Button>
        <Button component={Link} to="/disciplinary-cases" variant="outlined" size="small" endIcon={<ArrowForward />}>
          View Disciplinary
        </Button>
        <Button component={Link} to="/pips" variant="outlined" size="small" endIcon={<ArrowForward />}>
          View PIPs
        </Button>
        <Button component={Link} to="/audit-logs" variant="outlined" size="small" startIcon={<Security />}>
          View Audit Log
        </Button>
      </Box>
    </Box>
  );
};

export const TerminationShow = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    return <TerminationShowContent record={record} />;
  }} />
);

export const TerminationCreate = () => {
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get('employeeId') || '';

  return (
    <Create>
      <SimpleForm>
        <Alert severity="info" sx={{ mb: 2 }}>
          Complete all fields — BCEA Section 38 requires written notice of termination
        </Alert>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Employee & Termination Details</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <ReferenceInput source="employee.id" reference="employees" fullWidth defaultValue={employeeId}>
            <SelectInput optionText="name" label="Employee" />
          </ReferenceInput>
        <SelectInput source="type" choices={TERMINATION_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <DateField source="effectiveDate" label="Effective Date (Last Working Day)" />
        <DateField source="noticeDate" label="Notice Served Date" />
        <TextInput source="noticePeriod" label="Notice Period (weeks)" fullWidth />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Reason & Legal Reference</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="reason" multiline fullWidth label="Reason for Termination" />
        <SelectInput source="legalReference" choices={['LRA Schedule 8', 'BCEA Section 38', 'LRA Section 189', 'Contract Clause', 'Mutual Agreement'].map(l => ({ id: l, name: l }))} fullWidth label="Legal Reference" />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Probation & Clearance</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        <SelectInput source="probationOutcome" choices={['Confirmed', 'Extended', 'Not Applicable'].map(p => ({ id: p, name: p }))} fullWidth label="Probation Outcome" />
        <SelectInput source="ui19Issued" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]} fullWidth label="UI19 Issued" />
        <SelectInput source="certificateOfServiceIssued" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]} fullWidth label="Certificate of Service Issued" />
      </Box>
      <SelectInput source="status" choices={TERMINATION_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Initiated" sx={{ mt: 2 }} />
      <TextInput source="additionalNotes" multiline fullWidth label="Additional Notes" sx={{ mt: 1 }} />
    </SimpleForm>
  </Create>
  );
};

export const TerminationEdit = () => (
  <Edit>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Update status as offboarding progresses — UI19 and Certificate required before finalization
      </Alert>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Offboarding Status</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        <SelectInput source="status" choices={TERMINATION_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
        <SelectInput source="type" choices={TERMINATION_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <DateField source="effectiveDate" label="Effective Date" />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Statutory Documents</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        <SelectInput source="ui19Issued" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]} fullWidth label="UI19 Issued" />
        <SelectInput source="certificateOfServiceIssued" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]} fullWidth label="Certificate of Service Issued" />
        <DateField source="documentsIssuedDate" label="Documents Issued Date" />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Exit Checklist</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <SelectInput source="laptopReturned" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }, { id: 'N/A', name: 'N/A' }]} fullWidth label="Laptop Returned" />
        <SelectInput source="accessCardReturned" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }, { id: 'N/A', name: 'N/A' }]} fullWidth label="Access Card Returned" />
        <SelectInput source="systemDeactivated" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }, { id: 'N/A', name: 'N/A' }]} fullWidth label="System De-provisioned" />
        <SelectInput source="exitInterviewCompleted" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }, { id: 'Scheduled', name: 'Scheduled' }]} fullWidth label="Exit Interview" />
        <SelectInput source="finalPayApproved" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }, { id: 'Pending', name: 'Pending' }]} fullWidth label="Final Pay Approved" />
        <SelectInput source="handOverCompleted" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }, { id: 'N/A', name: 'N/A' }]} fullWidth label="Hand-over Completed" />
      </Box>
      <TextInput source="additionalNotes" multiline fullWidth label="Additional Notes" sx={{ mt: 2 }} />
    </SimpleForm>
  </Edit>
);

const ExitInterviewContent = ({ record }: { record: ExitInterviewRecord }) => {
  const employeeMap = useEmployeeMap();
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Exit Interview</Typography>
      <Grid container spacing={2}>
        {[
          { label: 'Employee', value: getEmployeeName(employeeMap, record.employee?.id) },
          { label: 'Scheduled Date', value: record.scheduledDate },
          { label: 'Interviewer', value: record.interviewerId },
          { label: 'Reason for Leaving', value: record.reasonForLeaving },
          { label: 'Additional Comments', value: record.additionalComments },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>{f.value ?? '—'}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const ExitInterview = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    return <ExitInterviewContent record={record} />;
  }} />
);

export const TerminationDetail = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Termination Details</Typography>
        <Grid container spacing={2}>
          {[
            { label: 'Type', value: record.terminationType },
            { label: 'Status', value: record.status },
            { label: 'Effective Date', value: record.effectiveDate },
            { label: 'UI19 Number', value: record.ui19Number },
            { label: 'Certificate Issued', value: record.certificateOfServiceIssued ? 'Yes' : 'No' },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>{f.value ?? '—'}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }} />
);